import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Portal } from '@/components/Portal';
import { useDismissable } from '@/lib/useDismissable';
import { useDmFolders } from '@/lib/useDmFolders';
import { EditIcon, TrashIcon } from '@/components/icons';

/**
 * Right-click management menu for a folder chip: rename (inline) or delete.
 * Cursor-anchored (Portal + clamp), mirroring DmFolderContextMenu. Deleting a
 * folder only removes the organisational view — the DM channels themselves are
 * untouched (the server cascades dm_folder_entries, not channels).
 */
export function FolderManageMenu(props: {
  folderId: string;
  folderName: string;
  x: number;
  y: number;
  onClose: () => void;
  /** Called after a delete so the parent can reset the active tab to "Все". */
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(props.folderName);
  const { renameFolder, deleteFolder, isMutating } = useDmFolders();

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const m = 8;
    let nx = props.x;
    let ny = props.y;
    if (nx + r.width + m > window.innerWidth) nx = props.x - r.width;
    if (ny + r.height + m > window.innerHeight) ny = window.innerHeight - r.height - m;
    nx = Math.min(Math.max(m, nx), window.innerWidth - r.width - m);
    ny = Math.max(m, ny);
    setPos({ x: nx, y: ny });
  }, [props.x, props.y, renaming]);

  useDismissable(true, ref, props.onClose);

  function submitRename() {
    const trimmed = name.trim();
    if (trimmed && trimmed !== props.folderName) renameFolder(props.folderId, trimmed);
    setRenaming(false);
    props.onClose();
  }

  return (
    <Portal>
      <div
        ref={ref}
        style={
          pos ? { left: pos.x, top: pos.y } : { left: -9999, top: -9999, visibility: 'hidden' }
        }
        className="bg-surface fixed z-[100] min-w-[200px] overflow-hidden rounded-xl border border-separator py-1.5 shadow-card"
        role="menu"
      >
        {renaming ? (
          <form
            className="px-2 py-1"
            onSubmit={(e) => {
              e.preventDefault();
              submitRename();
            }}
          >
            <input
              autoFocus
              value={name}
              maxLength={40}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setRenaming(false);
                  props.onClose();
                }
              }}
              placeholder={t('dm.folderName')}
              className="w-full rounded-lg border border-separator bg-default/40 px-2.5 py-1.5 text-sm text-ink outline-none placeholder:text-ink-muted focus:border-accent"
            />
          </form>
        ) : (
          <button
            type="button"
            role="menuitem"
            disabled={isMutating}
            onClick={() => setRenaming(true)}
            className="group/ctx mx-1 flex w-[calc(100%-0.5rem)] items-center gap-3 rounded-lg px-3 py-1.5 text-left text-sm text-ink transition-colors hover:bg-white/8 disabled:opacity-50"
          >
            <EditIcon className="h-4 w-4 shrink-0 text-ink-muted transition-colors group-hover/ctx:text-accent" />
            <span className="flex-1 truncate">{t('common.rename')}</span>
          </button>
        )}

        <button
          type="button"
          role="menuitem"
          disabled={isMutating}
          onClick={() => {
            deleteFolder(props.folderId);
            props.onDeleted();
            props.onClose();
          }}
          className="group/ctx mx-1 flex w-[calc(100%-0.5rem)] items-center gap-3 rounded-lg px-3 py-1.5 text-left text-sm text-danger transition-colors hover:bg-danger/10 disabled:opacity-50"
        >
          <TrashIcon className="h-4 w-4 shrink-0" />
          <span className="flex-1 truncate">{t('dm.deleteFolder')}</span>
        </button>
      </div>
    </Portal>
  );
}
